import { AgentChannels, FlowTypes, TriggerType } from 'v2/types'
import { useFlowStore } from '../store'
import { FlowNodeTypes } from '../types'
import { getChannelTypeFromFlowType } from '../utils'

type FlowInstance = ReturnType<typeof useFlowStore.getState>['flowInstance']

const readStartTriggers = (flowInstance: FlowInstance): string[] => {
  const startNode = flowInstance?.getNodes().find((node) => node.type === FlowNodeTypes.start)
  const startTriggers = (startNode?.data?.config as { triggers?: unknown } | undefined)?.triggers
  return Array.isArray(startTriggers)
    ? startTriggers.filter((trigger): trigger is string => typeof trigger === 'string')
    : []
}

const OUTBOUND_TRIGGERS = new Set<string>([
  TriggerType.outbound_call,
  TriggerType.outbound_message,
  TriggerType.outbound_whatsapp,
])
const OUTBOUND_FLOW_TYPES = new Set<string>([
  FlowTypes.outgoing_call,
  FlowTypes.outgoing_sms,
  FlowTypes.outgoing_whatsapp,
])

// Outbound flows are out of scope for sub account tagging, so the control is not offered on them.
export const isOutboundFlow = (triggers: string[], flowType: string | null | undefined) =>
  triggers.length
    ? triggers.some((trigger) => OUTBOUND_TRIGGERS.has(trigger))
    : !!flowType && OUTBOUND_FLOW_TYPES.has(flowType)

export const useIsOutboundFlow = () => {
  const currentFlowType = useFlowStore((state) => state.currentFlowType)
  const flowInstance = useFlowStore((state) => state.flowInstance)
  return isOutboundFlow(readStartTriggers(flowInstance), currentFlowType)
}

interface AgentSubaccountControlProps {
  currentSubAuthId: string | null
  disabled?: boolean
}

// Exported so the test can assert on the copy itself, not a duplicate of it. Deliberately a
// directive rather than an explanation: the number-save path already rejects this pair with a
// 422, so stating the rule beats describing the billing split. Never claim calls "will fail"
// — cross-subaccount runs work; see the hook doc below before editing this wording.
export const SUBACCOUNT_LINK_WARNING = {
  title: "Check this agent's numbers",
  content:
    'Please note: The phone number linked to this agent must not be tagged to a different sub account',
} as const

// WhatsApp numbers carry no sub account of their own; the pairing sits on the WhatsApp
// Business Account and covers every number under it, so this variant names that account
// and where its mapping is changed instead of "the phone number".
export const SUBACCOUNT_WHATSAPP_LINK_WARNING = {
  title: "Check this agent's WhatsApp account",
  content:
    'The WhatsApp Business Account linked to this agent must not be mapped to a different sub account. The mapping covers the whole business account, not one number, and is set under WhatsApp Configuration → Configure Number → Account Mapping.',
} as const

// An omni agent can be triggered by WhatsApp and by a phone-number channel at once, and
// then the user has both pairings to check, so the copy names both.
export const SUBACCOUNT_MIXED_LINK_WARNING = {
  title: "Check this agent's numbers and WhatsApp account",
  content:
    'A phone number linked to this agent must not be tagged to a different sub account, and neither must the WhatsApp Business Account behind its WhatsApp trigger. That mapping covers the whole business account and is set under WhatsApp Configuration → Configure Number → Account Mapping.',
} as const

/**
 * State + handlers for the agent Sub Account tag control. Extracted from the
 * component so the select behaviour is testable without driving the Neo Select's
 * Radix popover (which doesn't open under happy-dom).
 *
 * Selecting a subaccount writes the tag into agentSettings so it rides the NORMAL
 * flow save, exactly like timezone / voice_ai_config — the flow then shows unsaved
 * changes via the standard agentSettings compare and the main Save/Publish button
 * persists it in the wholesale save payload; config-service validates a change
 * server-side (parent-ownership). Navigating away without Save
 * discards it (agentSettings resets on flow change).
 *
 * A staged tag surfaces an inline warning rather than a confirm dialog, so the user is
 * told at pick time — the server can't enumerate a flow's numbers, so it can't raise
 * this itself.
 *
 * The warning is about ATTRIBUTION, not failure. An earlier version claimed a number on
 * a different subaccount "will fail"; live testing (July 2026) disproved it — inbound
 * calls, call forward and the outbound Call API all succeed cross-subaccount, because
 * the voice path is XML we hand back to Plivo, which executes it under the A-leg's own
 * account and never checks the tag. What actually happens is that telecom charges follow
 * the number's account while run and AI charges follow the tag, so the reporting splits
 * silently. Keep the copy on that consequence; do not reintroduce a failure claim.
 *
 * The warning's copy depends on the trigger: every channel but WhatsApp pairs a sub
 * account to each phone number, while WhatsApp pairs it to the Business Account
 * (WhatsApp settings → Account Mapping), so a WhatsApp trigger names that instead.
 * The triggers come off the Start node rather than the flow type, because an omni agent
 * has several and no single type — and one carrying both kinds gets both rules.
 */
export const useSubaccountTagControl = ({ currentSubAuthId }: AgentSubaccountControlProps) => {
  const setAgentSettings = useFlowStore((state) => state.setAgentSettings)
  const currentFlowType = useFlowStore((state) => state.currentFlowType)
  const flowInstance = useFlowStore((state) => state.flowInstance)

  // The dropdown shows the tag staged on agentSettings — the exact value the next save
  // sends — rather than keeping a copy of its own. With one source of truth the account
  // on screen can never drift from the one Save persists: not when the server baseline
  // lands after mount, and not when a save's refetch arrives after a newer pick.
  // (useAgentSettings seeds it from the server per flow, so a saved tag shows on load.)
  const stagedSubAuthId = useFlowStore((state) => state.agentSettings?.sub_auth_id ?? null)
  const value = stagedSubAuthId ?? 'Nil'

  // Write the tag into agentSettings so it rides the normal flow save (like
  // timezone / voice_ai_config); config-service validates a change server-side.
  const applyTag = (tag: string | null) => {
    const current = useFlowStore.getState().agentSettings
    if (current) setAgentSettings({ ...current, sub_auth_id: tag })
  }

  const onSelect = (next: string | string[]) => {
    const selected = Array.isArray(next) ? next[0] : next
    applyTag(selected === 'Nil' ? null : selected)
  }

  // Warn only while a NEW subaccount is staged and unsaved. Untag ('Nil') moves
  // attribution back to the main account for everything, so there is no split to warn
  // about, and the warning clears on its own once the save lands and currentSubAuthId
  // catches up.
  const showLinkWarning = value !== 'Nil' && value !== currentSubAuthId

  // An omni agent has several triggers and no single flow type ('omni' reads as call
  // through getChannelTypeFromFlowType), so the Start node's own trigger list decides.
  const triggers = readStartTriggers(flowInstance)
  const hasWhatsappTrigger = triggers.length
    ? triggers.some(
        (trigger) => trigger === TriggerType.whatsapp || trigger === TriggerType.outbound_whatsapp,
      )
    : !!currentFlowType &&
      getChannelTypeFromFlowType(currentFlowType as FlowTypes) === AgentChannels.whatsapp
  // An omni agent can carry both, and then both rules apply: WhatsApp pairs the sub account
  // to the Business Account, every other channel pairs it to the phone number.
  const hasNumberTrigger = triggers.some(
    (trigger) => trigger !== TriggerType.whatsapp && trigger !== TriggerType.outbound_whatsapp,
  )
  const linkWarning = !hasWhatsappTrigger
    ? SUBACCOUNT_LINK_WARNING
    : hasNumberTrigger
      ? SUBACCOUNT_MIXED_LINK_WARNING
      : SUBACCOUNT_WHATSAPP_LINK_WARNING

  return {
    value,
    showLinkWarning,
    linkWarning,
    onSelect,
  }
}
